// Support report generation: gathers app/runtime/printing diagnostics into a
// redacted JSON payload the user can copy, download or share with support.
// Modeled on cartadigital-pos app/composables/useSupportReport.ts.
import { ref } from 'vue';
import { Capacitor } from '@capacitor/core';
import { Directory, Encoding, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { saveAs } from 'file-saver';
import { Globals } from '@/modules/Globals';
import { vueData } from '@/VueData';
import { AlertType } from '@/interfaces/IAlert';
import { useUi } from '@/composition/use-ui';
import { useAuthStore } from '@/composition/use-auth';
import { useSocketStore } from '@/composition/use-socket';
import { usePrintingStore } from '@/composition/use-printing';
import { usePrintingV2Store } from '@/stores/printing-v2.store';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { getErrorHistory } from '@/lib/support/error-history';
import {
  safeAsync,
  sanitizeString,
  stringifyForReport,
} from '@/lib/support/support-report';
import { isShareCancelledError } from '@/lib/invoicing/deliver-pdf';
import {
  SecondaryDisplayDiagnostics,
  isSecondaryDisplayDiagnosticsAvailable,
} from '@/lib/capacitor/SecondaryDisplayDiagnostics';

export type ISupportReportSource =
  | 'printing_help'
  | 'native_printing_modal'
  | 'native_printing_help';

function tryGetTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function tryGetNavigatorConnection(): Record<string, unknown> | null {
  if (typeof navigator === 'undefined') return null;
  const anyNav = navigator as unknown as { connection?: unknown };
  const conn = anyNav.connection;
  if (!isRecord(conn)) return null;

  const pick = (k: string): unknown => (k in conn ? conn[k] : undefined);
  const out: Record<string, unknown> = {
    effectiveType: pick('effectiveType'),
    downlink: pick('downlink'),
    rtt: pick('rtt'),
    saveData: pick('saveData'),
    type: pick('type'),
  };

  for (const [k, v] of Object.entries(out)) {
    if (v === undefined) delete out[k];
  }

  return Object.keys(out).length ? out : null;
}

function downloadJson(filename: string, text: string): void {
  saveAs(new Blob([text], { type: 'application/json' }), filename);
}

export function useSupportReport() {
  const ui = useUi();
  const authStore = useAuthStore();
  const socketStore = useSocketStore();
  const printingStore = usePrintingStore();
  const printingV2Store = usePrintingV2Store();
  const restaurantStore = useRestaurantStore();

  const isBuilding = ref(false);

  function buildPrintingSnapshot(): Record<string, unknown> {
    const strategy = printingStore.getStrategy();
    const strategyKind = Globals.isCapacitor
      ? 'native'
      : printingStore.bridgeMode === 'bridge'
      ? 'bridge'
      : 'legacy-plugin';

    const v2Config = printingV2Store.displayConfig;
    const v2Summary = v2Config
      ? {
          schemaVersion: v2Config.schemaVersion,
          savedAt: v2Config.savedAt,
          migratedFromV1: v2Config.migratedFromV1,
          assignments: v2Config.assignments,
          unresolvedV1: v2Config.unresolvedV1,
          logicalPrinters: v2Config.logicalPrinters.map((lp) => ({
            id: lp.id,
            name: lp.name,
            ref: lp.ref,
            transport: lp.transport,
            format: lp.format,
            paperWidthMm: lp.paperWidthMm,
            codepage: lp.codepage,
            cutMode: lp.cutMode ?? 'modern',
          })),
        }
      : null;

    return {
      strategyKind,
      strategyStatus: strategy.status.value,
      bridgeMode: printingStore.bridgeMode,
      pluginVersion: printingStore.pluginVersion,
      discoveredPrinters: (printingStore.printers ?? []).map((p) => ({
        id: p.id,
        name: p.name,
        type: p.type,
      })),
      assignments: {
        ticket: printingStore.ticketPrinter?.name ?? null,
        sticker: printingStore.stickerPrinter?.name ?? null,
        comandaByStation: Object.fromEntries(
          Object.entries(printingStore.comandaPrintersByStation).map(
            ([stationId, printer]) => [stationId, printer?.name ?? null]
          )
        ),
      },
      settings: {
        ticket: printingStore.ticketPrintingSettings,
        comanda: printingStore.comandaPrintingSettings,
        sticker: printingStore.stickerPrintingSettings,
      },
      v2Config: v2Summary,
      // Raw bytes excluded on purpose: they are huge and useless in a report.
      lastJobs: printingStore.lastPrintJobs.map((job) => ({
        printerType: job.printerType,
        comandaStationId: job.comandaStationId ?? null,
        title: job.title,
        subtitle: job.subtitle ?? null,
        status: job.status,
        errorMessage: job.errorMessage ?? null,
        byteCount: job.data?.length ?? 0,
        createdAt: job.createdAt,
        metadata: job.metadata ?? null,
      })),
    };
  }

  async function buildSupportReport(
    source: ISupportReportSource
  ): Promise<Record<string, unknown>> {
    const createdAtMs = Date.now();

    const secondaryDisplayDiagnostics = isSecondaryDisplayDiagnosticsAvailable()
      ? await safeAsync(
          async () =>
            await SecondaryDisplayDiagnostics.getDiagnostics({
              runPresentationTests: false,
            })
        )
      : null;

    return {
      meta: {
        type: 'admin_support_report',
        schemaVersion: 1,
        source,
        createdAt: new Date(createdAtMs).toISOString(),
        createdAtMs,
        appRoute:
          typeof window !== 'undefined'
            ? `${window.location.pathname}${window.location.search}${window.location.hash}`
            : null,
      },
      app: { version: Globals.version },
      runtime: {
        isCapacitor: Globals.isCapacitor,
        capacitorPlatform: Capacitor.getPlatform(),
        userAgent:
          typeof navigator !== 'undefined' ? navigator.userAgent : null,
        language: typeof navigator !== 'undefined' ? navigator.language : null,
        timeZone: tryGetTimeZone(),
        connection: tryGetNavigatorConnection(),
        isOnline: typeof navigator !== 'undefined' ? navigator.onLine : null,
      },
      scope: {
        restaurantId: restaurantStore.restaurant?.id ?? null,
        branchId: restaurantStore.selectedBranch?.id ?? null,
        userId: authStore.user?.id ?? null,
      },
      socket: { isConnected: Boolean(socketStore.isConnected) },
      printing: buildPrintingSnapshot(),
      errorHistory: getErrorHistory(),
      ...(secondaryDisplayDiagnostics
        ? {
            secondaryDisplayDiagnostics: secondaryDisplayDiagnostics.ok
              ? secondaryDisplayDiagnostics.value
              : { error: secondaryDisplayDiagnostics.error },
          }
        : {}),
    };
  }

  async function buildReportText(
    source: ISupportReportSource
  ): Promise<string | null> {
    isBuilding.value = true;
    try {
      const report = await buildSupportReport(source);
      return stringifyForReport(report, { maxArrayItems: 200 });
    } catch (e) {
      console.error('[SupportReport] build failed', e);
      vueData.logException(e, 'Support report build');
      ui.showToast(AlertType.Error, 'No se pudo generar el reporte');
      return null;
    } finally {
      isBuilding.value = false;
    }
  }

  async function copyReport(source: ISupportReportSource): Promise<boolean> {
    const text = await buildReportText(source);
    if (!text) return false;

    try {
      await navigator.clipboard.writeText(text);
      ui.showToast(AlertType.Success, 'Reporte copiado para soporte');
      return true;
    } catch (e) {
      ui.serverError(e, 'No se pudo copiar el reporte');
      return false;
    }
  }

  async function downloadReport(source: ISupportReportSource): Promise<void> {
    const text = await buildReportText(source);
    if (!text) return;

    try {
      downloadJson(`admin-support-report-${Date.now()}.json`, text);
      ui.showToast(AlertType.Success, 'Reporte descargado');
    } catch (e) {
      ui.serverError(e, 'No se pudo descargar el reporte');
    }
  }

  /**
   * Capacitor: temp .txt + native share sheet (a .txt attachment survives
   * share-target mime filters that drop application/json). Web: download.
   */
  async function shareReport(source: ISupportReportSource): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      await downloadReport(source);
      return;
    }

    const text = await buildReportText(source);
    if (!text) return;

    // Legacy shell (old APK without Share/Filesystem): copy is the only
    // delivery that works — the WebView cannot download blobs either.
    if (
      !Capacitor.isPluginAvailable('Share') ||
      !Capacitor.isPluginAvailable('Filesystem')
    ) {
      try {
        await navigator.clipboard.writeText(text);
        ui.showToast(AlertType.Success, 'Reporte copiado para soporte');
      } catch (e) {
        ui.serverError(e, 'No se pudo copiar el reporte');
      }
      return;
    }

    try {
      const can = await Share.canShare();
      if (!can.value) {
        ui.showToast(
          AlertType.Info,
          'Compartir no está disponible en este dispositivo'
        );
        await navigator.clipboard.writeText(text);
        ui.showToast(AlertType.Success, 'Reporte copiado para soporte');
        return;
      }

      const filename = `admin-support-report-${Date.now()}.txt`;
      const result = await Filesystem.writeFile({
        path: `support/${filename}`,
        directory: Directory.Temporary,
        data: text,
        encoding: Encoding.UTF8,
        recursive: true,
      });

      await Share.share({
        title: 'Reporte de soporte',
        text: sanitizeString(
          [
            'Reporte de soporte del panel',
            `Fecha: ${new Date().toISOString()}`,
            'Adjunto el reporte completo como .txt (formato JSON).',
          ].join('\n'),
          4000
        ),
        files: [result.uri],
        dialogTitle: 'Compartir reporte',
      });
      ui.showToast(AlertType.Success, 'Listo. Se abrió el menú de compartir.');
    } catch (e) {
      if (isShareCancelledError(e)) return;
      ui.serverError(e, 'No se pudo compartir el reporte');
    }
  }

  return {
    isBuilding,
    copyReport,
    downloadReport,
    shareReport,
  };
}
